import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  getGridStringOperators,
  getGridDateOperators,
  type GridColDef,
  type GridFilterOperator,
  type GridFilterModel,
  type GridRenderCellParams,
  type GridRowHeightParams,
  type GridSelectionModel,
  type GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  type OwnerRelatedAcquireOption,
  type OwnerRelatedRegistriesQuery,
  type OwnerRelatedRegistryResponse,
  type OwnerRelatedRegistrySelection,
} from "@/types/ownerRelatedRegistries";
import {
  createOwnerRelatedRegistryId,
  defaultAcquireOptionForRegistry,
  getAcquireOptionLabel,
  getPictureTypeLabel,
} from "@/features/acquireBook/utils/ownerRelatedRegistries";
import { Button, ButtonVariantOption } from "@/components/Button";
import { convertBookTypeToJpn } from "@/utils/utilsAcquireBook";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface OwnerRelatedContext {
  realEstateTypeLabel: string;
  prefName: string;
  cityName: string;
  locationLabel: string;
  chibanKaokuNumber: string;
}

interface OwnerRelatedRegistriesModalProps {
  open: boolean;
  onClose: () => void;
  context: OwnerRelatedContext;
  query: OwnerRelatedRegistriesQuery | null;
  queryKey: string | null;
  initialSelection: OwnerRelatedRegistrySelection[];
  search: (
    params: OwnerRelatedRegistriesQuery
  ) => Promise<OwnerRelatedRegistryResponse[]>;
  reset: () => void;
  isLoading: boolean;
  cachedResult: {
    queryKey: string;
    rows: OwnerRelatedRegistryResponse[];
  } | null;
  onSubmit: (selection: OwnerRelatedRegistrySelection[]) => void;
  onSearchCompleted?: (
    query: OwnerRelatedRegistriesQuery,
    rows: OwnerRelatedRegistryResponse[]
  ) => void;
}

interface ModalRow extends OwnerRelatedRegistryResponse, GridValidRowModel {}

type ModalGridRenderCellParams<V = any> = GridRenderCellParams<V, ModalRow>;

const realEstateTypeOptions = [
  { value: "LAND", label: convertBookTypeToJpn("LAND") },
  { value: "BUILDING", label: convertBookTypeToJpn("BUILDING") },
  { value: "CONDOMINIUM", label: convertBookTypeToJpn("CONDOMINIUM") },
];

const pictureTypeOptions = [
  { value: "SHOYUSHAJIKO", label: getPictureTypeLabel("SHOYUSHAJIKO") },
  { value: "ZENBUJIKO", label: getPictureTypeLabel("ZENBUJIKO") },
];

const bulkOptions: Array<{ value: OwnerRelatedAcquireOption; label: string }> =
  [
    { value: "reuse", label: getAcquireOptionLabel("reuse") },
    { value: "new", label: getAcquireOptionLabel("new") },
    { value: "retained", label: getAcquireOptionLabel("retained") },
  ];

const bulkSelectionOptions: Array<{
  value: OwnerRelatedAcquireOption;
  label: string;
}> = [
  { value: "reuse", label: "全てリユースにする" },
  { value: "new", label: "全て新規にする" },
  { value: "retained", label: "全て取得済みにする" },
];

const getAvailableAcquireOptions = (
  row: OwnerRelatedRegistryResponse
): OwnerRelatedAcquireOption[] => {
  const options: OwnerRelatedAcquireOption[] = [];
  if (row.reuseRegistry) {
    options.push("reuse");
  }
  options.push("new");
  if (row.retainedRegistry) {
    options.push("retained");
  }
  return options;
};

const parseRegistryDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatRegistryDate = (value: Date | null): string => {
  if (!value) return "-";
  return `${value.getFullYear()}/${String(value.getMonth() + 1).padStart(
    2,
    "0"
  )}/${String(value.getDate()).padStart(2, "0")}`;
};

const Toolbar: React.FC = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: "flex-end", pr: 1, py: 0.5 }}>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
};

export const OwnerRelatedRegistriesModal: React.FC<
  OwnerRelatedRegistriesModalProps
> = ({
  open,
  onClose,
  context,
  query,
  initialSelection,
  search,
  reset,
  isLoading,
  cachedResult,
  queryKey,
  onSearchCompleted,
  onSubmit,
}) => {
  const [rows, setRows] = useState<ModalRow[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [rowOptions, setRowOptions] = useState<
    Record<string, OwnerRelatedAcquireOption>
  >({});
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const lastFetchedQueryKeyRef = useRef<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [bulkOption, setBulkOption] = useState<OwnerRelatedAcquireOption | "">(
    ""
  );

  const availableBulkSelectionOptions = useMemo(() => {
    const hasReuse = rows.some((row) => row.reuseRegistry);
    const hasRetained = rows.some((row) => row.retainedRegistry);
    return bulkSelectionOptions.filter((option) => {
      if (option.value === "reuse") {
        return hasReuse;
      }
      if (option.value === "retained") {
        return hasRetained;
      }
      return true;
    });
  }, [rows]);

  const getRowHeight = useCallback(({ model }: GridRowHeightParams) => {
    const typedModel = model as ModalRow;
    const owners = Array.isArray(typedModel.owners) ? typedModel.owners : [];
    const displayOwners = owners.slice(0, 3);
    const CHAR_PER_LINE = 16;

    const estimateLinesForOwner = (text: string): number => {
      if (text.trim() === "") {
        return 1;
      }
      return Math.max(1, Math.ceil(text.length / CHAR_PER_LINE));
    };

    const ownerLines = displayOwners.reduce(
      (lines, owner) => lines + estimateLinesForOwner(owner),
      0
    );

    const additionalLine = owners.length > displayOwners.length ? 1 : 0;
    const totalLines = Math.max(1, ownerLines + additionalLine);
    const lineHeight = 22;
    const verticalPadding = 16; // matching cell paddings
    const calculatedHeight = totalLines * lineHeight + verticalPadding;
    const baseHeight = 46;
    return Math.max(baseHeight, calculatedHeight);
  }, []);

  const initialSelectionMap = useMemo(() => {
    return new Map<string, OwnerRelatedAcquireOption>(
      initialSelection.map((item) => [item.id, item.acquireOption])
    );
  }, [initialSelection]);

  const applyInitialSelection = useCallback(
    (items: OwnerRelatedRegistryResponse[]) => {
      const nextSelection: GridSelectionModel = [];
      const nextOptions: Record<string, OwnerRelatedAcquireOption> = {};

      items.forEach((item) => {
        const id = createOwnerRelatedRegistryId(item);
        const existing = initialSelectionMap.get(id);
        const available = getAvailableAcquireOptions(item);
        const preferred = defaultAcquireOptionForRegistry(item);
        const resolvedOption =
          existing && available.includes(existing)
            ? existing
            : available.includes(preferred)
            ? preferred
            : available[0];
        nextOptions[id] = resolvedOption;
        if (existing !== undefined && available.includes(existing)) {
          nextSelection.push(id);
        }
      });

      setSelectionModel(nextSelection);
      setRowOptions(nextOptions);
    },
    [initialSelectionMap]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetch = async (): Promise<void> => {
      if (!query || !queryKey) {
        setRows([]);
        setSelectionModel([]);
        setRowOptions({});
        return;
      }

      if (cachedResult && cachedResult.queryKey === queryKey) {
        setRows(cachedResult.rows);
        applyInitialSelection(cachedResult.rows);
        lastFetchedQueryKeyRef.current = queryKey;
        return;
      }

      if (lastFetchedQueryKeyRef.current === queryKey) {
        return;
      }

      lastFetchedQueryKeyRef.current = queryKey;
      setIsFetching(true);
      try {
        const result = await search(query);
        setRows(result);
        applyInitialSelection(result);
        if (result.length === 0) {
          toast.info("関連する登記は見つかりませんでした");
        }
        onSearchCompleted?.(query, result);
      } catch (error) {
        setRows([]);
        setSelectionModel([]);
        setRowOptions({});
      } finally {
        setIsFetching(false);
      }
    };

    void fetch();
  }, [
    open,
    query,
    queryKey,
    cachedResult,
    search,
    applyInitialSelection,
    onSearchCompleted,
  ]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      setSelectionModel([]);
      setRowOptions({});
      setBulkOption("");
      reset();
    }
  }, [open, reset]);

  const selectionModelToIds = useCallback(
    (model: GridSelectionModel): string[] => {
      return Array.isArray(model) ? model.map((id) => String(id)) : [];
    },
    []
  );

  const handleRowOptionChange = useCallback(
    (id: string, value: OwnerRelatedAcquireOption) => {
      setRowOptions((prev) => {
        const row = rows.find(
          (item) => createOwnerRelatedRegistryId(item) === id
        );
        if (!row) {
          return prev;
        }
        const available = getAvailableAcquireOptions(row);
        if (!available.includes(value)) {
          return prev;
        }
        if (prev[id] === value) {
          return prev;
        }
        return {
          ...prev,
          [id]: value,
        };
      });
    },
    [rows]
  );

  const handleBulkOptionChange = useCallback(
    (value: OwnerRelatedAcquireOption) => {
      setRowOptions((prev) => {
        let hasChange = false;
        const next = { ...prev };
        const ids = selectionModelToIds(selectionModel);
        ids.forEach((id) => {
          const row = rows.find(
            (item) => createOwnerRelatedRegistryId(item) === id
          );
          if (!row) {
            return;
          }
          const available = getAvailableAcquireOptions(row);
          if (available.includes(value)) {
            if (next[id] !== value) {
              next[id] = value;
              hasChange = true;
            }
          } else {
            const fallback = available[0];
            if (fallback && next[id] !== fallback) {
              next[id] = fallback;
              hasChange = true;
            }
          }
        });
        return hasChange ? next : prev;
      });
    },
    [selectionModel, selectionModelToIds, rows]
  );

  const handleSubmit = useCallback(() => {
    const selectedIds = selectionModelToIds(selectionModel);
    const selectedRows = rows.filter((row) =>
      selectedIds.includes(createOwnerRelatedRegistryId(row))
    );
    const selections: OwnerRelatedRegistrySelection[] = selectedRows.map(
      (row) => {
        const id = createOwnerRelatedRegistryId(row);
        const available = getAvailableAcquireOptions(row);
        const preferred = defaultAcquireOptionForRegistry(row);
        const stored = rowOptions[id];
        const acquireOption =
          stored && available.includes(stored)
            ? stored
            : available.includes(preferred)
            ? preferred
            : available[0];
        return {
          id,
          acquireOption,
          registry: row,
        };
      }
    );
    onSubmit(selections);
    onClose();
  }, [
    rows,
    selectionModel,
    selectionModelToIds,
    rowOptions,
    onSubmit,
    onClose,
  ]);

  const baseColumnProps = useMemo(
    () => ({
      align: "center" as const,
      headerAlign: "center" as const,
      sortable: true,
    }),
    []
  );

  const stringFilterOperators = useMemo(() => {
    const defaultOperators = getGridStringOperators();
    const allowedValues = new Set([
      "contains",
      "equals",
      "startsWith",
      "endsWith",
      "isEmpty",
      "isNotEmpty",
    ]);
    const baseOperators = defaultOperators.filter((operator) =>
      allowedValues.has(operator.value)
    );

    const result: GridFilterOperator[] = [];

    const containsOperator = defaultOperators.find(
      (operator) => operator.value === "contains"
    );
    if (containsOperator?.getApplyFilterFn) {
      result.push({
        ...containsOperator,
        getApplyFilterFn: containsOperator.getApplyFilterFn,
      });
      result.push({
        ...containsOperator,
        value: "doesNotContain",
        label: "...を含まない",
        getApplyFilterFn: (filterItem, column) => {
          const fn = containsOperator.getApplyFilterFn?.(filterItem, column);
          if (fn == null) return null;
          return (...args: Parameters<typeof fn>) => !fn(...args);
        },
      });
    }

    const equalsOperator = defaultOperators.find(
      (operator) => operator.value === "equals"
    );
    if (equalsOperator?.getApplyFilterFn) {
      result.push({
        ...equalsOperator,
        getApplyFilterFn: equalsOperator.getApplyFilterFn,
      });
      result.push({
        ...equalsOperator,
        value: "notEqual",
        label: "...に等しくない",
        getApplyFilterFn: (filterItem, column) => {
          const fn = equalsOperator.getApplyFilterFn?.(filterItem, column);
          if (fn == null) return null;
          return (...args: Parameters<typeof fn>) => !fn(...args);
        },
      });
    }

    baseOperators.forEach((operator) => {
      if (operator.value === "contains" || operator.value === "equals") return;
      result.push(operator);
    });

    return result;
  }, []);

  const dateFilterOperators = useMemo(() => {
    const defaultOperators = getGridDateOperators();
    const allowedValues = new Set([
      "after",
      "onOrAfter",
      "before",
      "onOrBefore",
      "is",
      "not",
      "isEmpty",
      "isNotEmpty",
    ]);
    return defaultOperators.filter((operator) =>
      allowedValues.has(operator.value)
    );
  }, []);

  const optionSelectSx = useMemo<SxProps<Theme>>(
    () => ({
      "& .MuiSelect-select": {
        py: 0.75,
        pl: 1.25,
        pr: 2.5,
      },
    }),
    []
  );

  const columns = useMemo((): Array<GridColDef<ModalRow>> => {
    return [
      {
        field: "selectionOption",
        headerName: "選択",
        width: 140,
        filterable: false,
        disableColumnMenu: true,
        ...baseColumnProps,
        sortable: false,
        renderCell: (
          params: ModalGridRenderCellParams<OwnerRelatedAcquireOption>
        ) => {
          const rowId = String(params.id);
          const available = getAvailableAcquireOptions(params.row);
          const stored =
            rowOptions[rowId] ?? defaultAcquireOptionForRegistry(params.row);
          const value = available.includes(stored) ? stored : available[0];
          return (
            <Box sx={{ width: "100%", display: "flex", py: 0.5 }}>
              <FormControl size="small" fullWidth>
                <Select<OwnerRelatedAcquireOption>
                  value={value}
                  onChange={(
                    event: SelectChangeEvent<OwnerRelatedAcquireOption>
                  ) => {
                    handleRowOptionChange(
                      rowId,
                      event.target.value as OwnerRelatedAcquireOption
                    );
                  }}
                  renderValue={(selected) =>
                    bulkOptions.find((opt) => opt.value === selected)?.label ??
                    ""
                  }
                  inputProps={{ "aria-label": "行ごとのアクション" }}
                  MenuProps={{
                    keepMounted: true,
                    sx: { "& .MuiPaper-root": { maxHeight: 240 } },
                  }}
                  sx={optionSelectSx}
                >
                  {available.map((option) => (
                    <MenuItem key={option} value={option}>
                      {bulkOptions.find((opt) => opt.value === option)?.label ??
                        ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          );
        },
      },
      {
        field: "realEstateType",
        headerName: "種別",
        width: 120,
        type: "singleSelect",
        valueOptions: realEstateTypeOptions,
        ...baseColumnProps,
        valueFormatter: (params) => {
          const option = realEstateTypeOptions.find(
            (item) => item.value === params.value
          );
          return option?.label ?? "";
        },
      },
      {
        field: "pictureType",
        headerName: "事項種別",
        width: 160,
        type: "singleSelect",
        valueOptions: pictureTypeOptions,
        ...baseColumnProps,
        valueFormatter: (params) => {
          const option = pictureTypeOptions.find(
            (item) => item.value === params.value
          );
          return option?.label ?? "";
        },
      },
      {
        field: "prefectureName",
        headerName: "都道府県",
        width: 150,
        ...baseColumnProps,
        valueGetter: (params) => params.row.prefectureName ?? "",
        filterOperators: stringFilterOperators,
      },
      {
        field: "addressText",
        headerName: "所在地",
        width: 280,
        ...baseColumnProps,
        valueGetter: (params) => params.row.addressText ?? "",
        filterOperators: stringFilterOperators,
      },
      {
        field: "chibanKaokuNumber",
        headerName: "地番 / 家屋番号",
        width: 180,
        ...baseColumnProps,
        valueGetter: (params) => {
          // 取得済み登記がある場合のみ地番家屋番号を表示
          // nullを返すことでフィルタでブランクとして認識される
          return params.row.retainedRegistry
            ? params.row.chibanKaokuNumber
            : null;
        },
        valueFormatter: (params): string => {
          // nullの場合はブランクとして表示
          return (params.value as string | null) ?? "";
        },
        filterOperators: stringFilterOperators,
      },
      {
        field: "owners",
        headerName: "所有者",
        width: 260,
        align: "left",
        headerAlign: "center",
        valueGetter: (params) => (params.row.owners ?? []).join("、"),
        filterOperators: stringFilterOperators,
        renderCell: (params) => {
          const owners = params.row.owners ?? [];
          if (owners.length === 0) return "-";
          const displayOwners = owners.slice(0, 3);
          const remaining = owners.length - displayOwners.length;
          return (
            <Box
              sx={{
                textAlign: "left",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              {displayOwners.map((owner, index) => (
                <Typography
                  key={`${params.id}-owner-${index}`}
                  variant="body2"
                  sx={{
                    display: "block",
                    width: "100%",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {owner}
                </Typography>
              ))}
              {remaining > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  その他{remaining}件
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "reusePictureReceivedDate",
        headerName: "リユース登記",
        width: 160,
        ...baseColumnProps,
        type: "date",
        valueGetter: (params) =>
          parseRegistryDate(params.row.reuseRegistry?.pictureReceivedDate),
        valueFormatter: (params) =>
          formatRegistryDate((params.value as Date | null) ?? null),
        filterOperators: dateFilterOperators,
      },
      {
        field: "retainedPictureReceivedDate",
        headerName: "取得済み登記",
        width: 160,
        ...baseColumnProps,
        type: "date",
        valueGetter: (params) =>
          parseRegistryDate(params.row.retainedRegistry?.pictureReceivedDate),
        valueFormatter: (params) =>
          formatRegistryDate((params.value as Date | null) ?? null),
        filterOperators: dateFilterOperators,
      },
    ];
  }, [
    baseColumnProps,
    optionSelectSx,
    rowOptions,
    handleRowOptionChange,
    stringFilterOperators,
    dateFilterOperators,
  ]);

  const loading = isLoading || isFetching;

  const handleSelectionModelChange = useCallback(
    (model: GridSelectionModel) => {
      setSelectionModel(model);
    },
    []
  );

  const selectedIds = useMemo(
    () => selectionModelToIds(selectionModel),
    [selectionModel, selectionModelToIds]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="owner-related-registries-modal"
    >
      <DialogTitle id="owner-related-registries-modal">
        同一の不動産オーナー名で名寄せ検索
      </DialogTitle>
      <DialogContent sx={{ minHeight: 700, overflowY: "hidden" }}>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Typography variant="body2">
            種別: {context.realEstateTypeLabel}
          </Typography>
          <Typography variant="body2">
            所在: {context.prefName}
            {context.cityName}
            {context.locationLabel}
          </Typography>
          <Typography variant="body2">
            地番/家屋番号: {context.chibanKaokuNumber}
          </Typography>
        </Stack>
        <Grid container alignItems="center" sx={{ mb: 1.5 }}>
          {loading && (
            <Grid item sx={{ mr: 1.5 }}>
              <CircularProgress size={18} />
            </Grid>
          )}
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <Select<OwnerRelatedAcquireOption | "">
                value={
                  availableBulkSelectionOptions.some(
                    (option) => option.value === bulkOption
                  )
                    ? bulkOption
                    : ""
                }
                onChange={(
                  event: SelectChangeEvent<OwnerRelatedAcquireOption | "">
                ) => {
                  const value = event.target.value as
                    | OwnerRelatedAcquireOption
                    | "";
                  setBulkOption(value);
                  if (value !== "") {
                    handleBulkOptionChange(value);
                  }
                }}
                size="small"
                displayEmpty
                renderValue={(selected) =>
                  selected === ""
                    ? "選択された行のアクション"
                    : availableBulkSelectionOptions.find(
                        (opt) => opt.value === selected
                      )?.label ?? ""
                }
                inputProps={{ "aria-label": "選択された行のアクション" }}
                MenuProps={{
                  keepMounted: true,
                  sx: { "& .MuiPaper-root": { maxHeight: 240 } },
                }}
                sx={optionSelectSx}
                disabled={availableBulkSelectionOptions.length === 0}
              >
                {availableBulkSelectionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ height: 540, width: "100%", overflowX: "auto" }}>
          <Box sx={{ maxWidth: "100%", height: "90%" }}>
            <StyledDataGrid
              rows={rows}
              columns={columns}
              getRowId={createOwnerRelatedRegistryId}
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectionModel}
              onSelectionModelChange={handleSelectionModelChange}
              loading={loading}
              getRowHeight={getRowHeight}
              headerHeight={40}
              components={{ Toolbar }}
              disableColumnFilter={false}
              filterMode="client"
              filterModel={filterModel}
              onFilterModelChange={setFilterModel}
              initialState={{
                pinnedColumns: { left: ["__check__", "selectionOption"] },
              }}
            />
          </Box>
        </Box>
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            選択件数: {formatNumberWithComma(selectedIds.length)}件
          </Typography>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          label="閉じる"
          onClick={onClose}
          variant={ButtonVariantOption.Outlined}
        />
        <Button
          label="取得対象に追加する"
          onClick={handleSubmit}
          disabled={selectedIds.length === 0}
          variant={ButtonVariantOption.Contained}
        />
      </DialogActions>
    </Dialog>
  );
};
